import React from 'react';
import {
  Box,
  Heading,
  Button,
  Flex,
  Text,
  Spinner,
  useToast,
  FormControl,
  FormLabel,
  Input,
  Select,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  InputGroup,
  InputLeftAddon,
} from '@chakra-ui/react';
import { FiUserPlus } from 'react-icons/fi';
import xhr from 'xhr';
import settings from '../../../../settings';
import { authHeaders } from 'utils/jwt';

const COUNTRIES = [
  { id: 53, name: 'Colombia', code: '+57' },
  { id: 241, name: 'Estados Unidos', code: '+1' },
  { id: 147, name: 'Mexico', code: '+52' },
  { id: 245, name: 'Venezuela', code: '+58' },
  { id: 68, name: 'Ecuador', code: '+593' },
  { id: 177, name: 'Peru', code: '+51' },
  { id: 49, name: 'Chile', code: '+56' },
  { id: 13, name: 'Argentina', code: '+54' },
  { id: 214, name: 'Espana', code: '+34' },
  { id: 174, name: 'Panama', code: '+507' },
  { id: 58, name: 'Costa Rica', code: '+506' },
  { id: 102, name: 'Honduras', code: '+504' },
  { id: 94, name: 'Guatemala', code: '+502' },
  { id: 28, name: 'Bolivia', code: '+591' },
  { id: 67, name: 'Republica Dominicana', code: '+1809' },
  { id: 176, name: 'Paraguay', code: '+595' },
  { id: 242, name: 'Uruguay', code: '+598' },
  { id: 70, name: 'El Salvador', code: '+503' },
  { id: 162, name: 'Nicaragua', code: '+505' },
  { id: 33, name: 'Brasil', code: '+55' },
];

export default function EnrollStudent({ schoolId }) {
  const [students, setStudents] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [creating, setCreating] = React.useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const [newStudent, setNewStudent] = React.useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    cellphone: '',
    country: 53,
  });

  React.useEffect(() => {
    fetchStudents();
  }, [schoolId]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const res = await xhr(
        `/students?filters[schoolFrom][id][$eq]=${schoolId}&populate=user&sort[0]=id:desc&pagination[limit]=50`
      );
      setStudents(res.data?.data || []);
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newStudent.first_name || !newStudent.email || !newStudent.password || !newStudent.cellphone) {
      toast({
        title: 'Campos requeridos',
        description: 'Nombre, email, celular y contrasena son obligatorios',
        status: 'warning',
        position: 'bottom',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    try {
      setCreating(true);

      // Con el JWT del staff el API no exige CAPTCHA (ver register-guard en klazia-api).
      const response = await fetch(`${settings.api}/api/auth/local/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          first_name: newStudent.first_name,
          last_name: newStudent.last_name,
          email: newStudent.email,
          password: newStudent.password,
          cellphone: newStudent.cellphone,
          country: newStudent.country,
          username: newStudent.email,
          user_type: 'student',
          schoolFrom: schoolId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMsg =
          errorData?.error?.message || 'Error al registrar estudiante';
        throw new Error(errorMsg);
      }

      toast({
        title: 'Estudiante matriculado exitosamente',
        description: `${newStudent.first_name} ${newStudent.last_name}`,
        status: 'success',
        position: 'bottom',
        duration: 4000,
        isClosable: true,
      });

      setNewStudent({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        cellphone: '',
        country: 53,
      });
      onClose();
      fetchStudents();
    } catch (error) {
      toast({
        title: 'Error al matricular estudiante',
        description: error.message || 'Intenta de nuevo',
        status: 'error',
        position: 'bottom',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCreating(false);
    }
  };

  const selectedCountry = COUNTRIES.find((c) => c.id === Number(newStudent.country));

  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="lg" />
      </Box>
    );
  }

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Heading size="sm">Estudiantes Matriculados</Heading>
        <Button leftIcon={<FiUserPlus />} colorScheme="purple" size="sm" onClick={onOpen}>
          Matricular Estudiante
        </Button>
      </Flex>

      <TableContainer bg="white" borderRadius="lg" boxShadow="sm">
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Nombre</Th>
              <Th>Email</Th>
              <Th>Celular</Th>
              <Th>Estado</Th>
            </Tr>
          </Thead>
          <Tbody>
            {students.map((s) => {
              const attrs = s.attributes || s;
              const user = attrs.user?.data?.attributes || attrs.user?.data || attrs.user;
              return (
                <Tr key={s.id}>
                  <Td>
                    {attrs.first_name || user?.first_name || ''}{' '}
                    {attrs.last_name || user?.last_name || ''}
                  </Td>
                  <Td>{user?.email || attrs.email || ''}</Td>
                  <Td>{attrs.cellphone || user?.cellphone || ''}</Td>
                  <Td>
                    <Badge colorScheme="green">Activo</Badge>
                  </Td>
                </Tr>
              );
            })}
            {students.length === 0 && (
              <Tr>
                <Td colSpan={4} textAlign="center" py={6} color="gray.500">
                  No hay estudiantes matriculados
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>

      {/* Modal: Matricular Estudiante */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Matricular Nuevo Estudiante</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction="column" gap={3}>
              <Flex gap={3}>
                <FormControl isRequired flex="1">
                  <FormLabel>Nombres</FormLabel>
                  <Input
                    value={newStudent.first_name}
                    onChange={(e) => setNewStudent((p) => ({ ...p, first_name: e.target.value }))}
                    placeholder="Nombres"
                  />
                </FormControl>
                <FormControl flex="1">
                  <FormLabel>Apellidos</FormLabel>
                  <Input
                    value={newStudent.last_name}
                    onChange={(e) => setNewStudent((p) => ({ ...p, last_name: e.target.value }))}
                    placeholder="Apellidos"
                  />
                </FormControl>
              </Flex>

              <Flex gap={3}>
                <FormControl flex="1">
                  <FormLabel>Pais</FormLabel>
                  <Select
                    value={newStudent.country}
                    onChange={(e) => setNewStudent((p) => ({ ...p, country: Number(e.target.value) }))}
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} ({c.code})
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl isRequired flex="1">
                  <FormLabel>Celular</FormLabel>
                  <InputGroup>
                    <InputLeftAddon>{selectedCountry?.code || '+57'}</InputLeftAddon>
                    <Input
                      type="tel"
                      value={newStudent.cellphone}
                      onChange={(e) =>
                        setNewStudent((p) => ({ ...p, cellphone: e.target.value.replace(/\D/g, '') }))
                      }
                      placeholder="Numero de celular"
                    />
                  </InputGroup>
                </FormControl>
              </Flex>

              <FormControl isRequired>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={newStudent.email}
                  onChange={(e) => setNewStudent((p) => ({ ...p, email: e.target.value }))}
                  placeholder="correo@ejemplo.com"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Contrasena</FormLabel>
                <Input
                  type="password"
                  value={newStudent.password}
                  onChange={(e) => setNewStudent((p) => ({ ...p, password: e.target.value }))}
                  placeholder="Contrasena para el estudiante"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  El estudiante usara este email y contrasena para ingresar a la plataforma
                </Text>
              </FormControl>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancelar
            </Button>
            <Button colorScheme="purple" onClick={handleCreate} isLoading={creating}>
              Matricular
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
